import { useEffect, useState } from 'react';
import Modal from '../../components/common/Modal';
import Field from '../../components/common/Field';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import useEventsStore from '../../store/useEventsStore';
import useParentsStore from '../../store/useParentsStore';
import { showToast } from '../../store/useToastStore';
import { fmtDate, fmtEventTime, todayISO } from '../../utils/formatters';
import { IconEnvelope } from '../../components/icons';

/* Invitation/announcement email — reviewed (subject + timing + preview) before it
   actually sends. One modal handles both send-now and schedule; "Send to Parents"
   opens it defaulted to Send Now, "Schedule Email" defaulted to Schedule. Until a
   scheduled date arrives the row's "Preview Email" reopens this, still editable.
   `pending` is { eventId, viewOnly?, preferSchedule? }. */
export default function InviteEmailModal({ pending, onClose }) {
  const ctx = useContextStore(selectCurrentContext);
  const { events, confirmInviteSend } = useEventsStore();
  const parents = useParentsStore((s) => s.parents);
  const [subject, setSubject] = useState('');
  const [sendMode, setSendMode] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');

  const ev = pending ? events.find((e) => e.id === pending.eventId) : null;

  useEffect(() => {
    if (pending && ev) {
      setSubject(ev.inviteSubject || `${ctx.name} event: ${ev.title}`);
      const hasSchedule = !ev.sent && !!ev.sendDate;
      setSendMode(hasSchedule || pending.preferSchedule ? 'schedule' : 'now');
      setScheduleDate(ev.sendDate || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  if (!pending || !ev) return <Modal open={false} onClose={onClose} title="Invitation Email" />;

  const kind = ev.mode === 'noRsvp' ? 'Announcement' : 'Invitation';
  const emailOnlyCount = parents.filter((p) => !p.onSprout).length;
  const timeStr = ev.startTime ? ` · ${fmtEventTime(ev.startTime, ev.endTime)}` : '';
  const scheduled = sendMode === 'schedule';

  const confirmSend = () => {
    if (scheduled) {
      if (!scheduleDate) { window.alert('Pick a date to schedule this for.'); return; }
      if (scheduleDate <= todayISO()) { window.alert('Pick a date after today — for today, just use Send Now.'); return; }
    }
    confirmInviteSend(ev.id, {
      subject: subject.trim() || 'Class Update',
      sendDate: scheduled ? scheduleDate : null,
    });
    showToast(
      scheduled
        ? `"${ev.title}" scheduled — sends ${fmtDate(scheduleDate)} ✓`
        : `"${ev.title}" ${kind.toLowerCase()} sent — also emailed to families not on the app ✓`
    );
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`${kind} Email`}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          {!pending.viewOnly && (
            <button className="btn btn-primary" onClick={confirmSend}>
              {scheduled ? `Schedule ${kind}` : `Send ${kind} Now`}
            </button>
          )}
        </>
      }
    >
      <div className="modal-to">
        <IconEnvelope />
        <span>
          To: <b>{emailOnlyCount} famil{emailOnlyCount === 1 ? 'y' : 'ies'}</b> not yet on the app — everyone else sees
          this as an in-app invite
        </span>
      </div>
      <Field label="Subject Line" style={{ marginBottom: 14 }}>
        <input type="text" value={subject} disabled={!!pending.viewOnly} onChange={(e) => setSubject(e.target.value)} />
      </Field>
      <Field label="Preview" />
      <div className="email-preview-card">
        {ev.image && <img className="banner" src={ev.image} alt="" />}
        <div className={`ep-overlap ${ev.image ? '' : 'no-image'}`}>
          <div className="evtitle">{ev.title}</div>
          <div className="ep-chips">
            <span className="ep-chip">{fmtDate(ev.date)}{timeStr}</span>
          </div>
        </div>
        <div className="inner">
          <div className="evdesc">{ev.desc}</div>
          {ev.mode !== 'noRsvp' && (
            <div className="rsvp-row">
              <span className="rsvp-btn yes">Yes, I'm going</span>
              <span className="rsvp-btn no">Can't make it</span>
            </div>
          )}
          <div className="foot">Sent from {ctx.name} on RoomParent.com.</div>
        </div>
      </div>
      {!pending.viewOnly && (
        <Field label="When Should This Send?" style={{ marginTop: 14, marginBottom: 0 }}>
          <div className="radio-row">
            <label>
              <input type="radio" name="inviteSendMode" value="now" checked={sendMode === 'now'} onChange={() => setSendMode('now')} />
              Send Now
            </label>
            <label>
              <input type="radio" name="inviteSendMode" value="schedule" checked={sendMode === 'schedule'} onChange={() => setSendMode('schedule')} />
              Schedule for a date
            </label>
          </div>
          {scheduled && (
            <div className="date-input-wrap" style={{ marginTop: 8 }}>
              <svg className="cal-ic" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
              <input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} />
            </div>
          )}
          <div className="hint">Sends automatically once that date arrives — no need to come back and hit send.</div>
        </Field>
      )}
    </Modal>
  );
}
